import { inject, Injectable } from '@angular/core';
import { InterpolatableTranslationObject } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { PlatformAppellation } from '../../models/structure/init';
import { InformationService } from '../../services/init/information.service';

/** A compiled translation: either a MessageFormat interpolation function or a plain string. */
type CompiledTranslation = ((params?: object) => string) | string;

/**
 * Translation value holding formal (Sie) and personal (du) variants of the same message.
 */
interface IAppellativeTranslation {
  /** Formal (Sie) variant. */
  formal?: unknown;
  /** Personal (du) variant. */
  personal?: unknown;
}

@Injectable()
export class AppellateTranslateCompiler extends TranslateMessageFormatCompiler {
  // Services
  private readonly informationService: InformationService = inject(InformationService);

  // Methods
  /**
   * Collapses `{ formal, personal }` translation objects into a single interpolation function
   * that resolves the variant matching the platform-wide form of address on render.
   * Needed because ngx-translate walks object values on its own and never passes them to the
   * parser, so an unresolved variant object would end up in the template as `[object Object]`.
   * The function reads the init signal, so the signal-based translate pipe re-renders on a changed form of address.
   * @param translations Raw translation value (string, object or array) to compile.
   * @param lang The language the translations belong to.
   * @returns The compiled translation, or an interpolation function for appellative values.
   */
  override compileTranslations(translations: unknown, lang: string): InterpolatableTranslationObject {
    if (!this.isAppellativeTranslation(translations)) return super.compileTranslations(translations, lang);

    const formal = this.compileVariant(translations.formal, lang);
    const personal = this.compileVariant(translations.personal, lang);

    const resolve = (params?: object): string => {
      const isPersonal = this.informationService.init()?.appellation?.toLowerCase() === ('personally' satisfies PlatformAppellation);
      const resolved = isPersonal ? (personal ?? formal) : (formal ?? personal);
      if (resolved === undefined) return '';
      return typeof resolved === 'function' ? resolved(params) : resolved;
    };
    // The MessageFormat compiler recurses through this method, so nested values may be functions; ngx-translate only types the root.
    return resolve as unknown as InterpolatableTranslationObject;
  }

  /**
   * Compiles a single appellative variant.
   * @param variant The variant value, may be missing in the translation file.
   * @param lang The language the translations belong to.
   * @returns The compiled variant, or undefined when the variant is not present.
   */
  private compileVariant(variant: unknown, lang: string): CompiledTranslation | undefined {
    if (variant === undefined) return undefined;
    return super.compileTranslations(variant, lang) as CompiledTranslation;
  }

  /**
   * Checks whether a translation value carries formal/personal variants.
   * @param value Translation value to inspect.
   * @returns `true` when at least one appellative variant is present.
   */
  private isAppellativeTranslation(value: unknown): value is IAppellativeTranslation {
    return !!value && typeof value === 'object' && !Array.isArray(value) && ('formal' in value || 'personal' in value);
  }
}
